import { deleteCookie, getCookie, getCookies, setCookie } from 'cookies-next';
import type { OptionsType } from 'cookies-next/lib/types';
import type { Dayjs } from 'dayjs';
import dateUtil from './dayjs';
import { seatingStartOrNull } from './utils/seatingTime';

/**
 * Set cookie
 *
 * @example
 * // On the client
 * set('foo', 'bar', null)
 * // On the server
 * set('foo', 'bar', null, { req, res })
 *
 * @see https://github.com/andreizanik/cookies-next/blob/master/README.md#client-and-server
 */
function set(name: string, value: string, days?: number, options?: OptionsType) {
  return setCookie(
    name,
    value,
    options || { expires: days ? dateUtil().add(days, 'days').toDate() : undefined },
  );
}

/**
 * Remove cookie
 * @example
 * // On the client
 * remove('foo')
 * // On the server
 * remove('foo', { req, res })
 * @see https://github.com/andreizanik/cookies-next/blob/master/README.md#client-and-server
 */

function remove(name: string, options?: OptionsType) {
  return deleteCookie(name, options);
}

/**
 * Get cookie
 *
 * @example
 * // On the client
 * get('foo')
 * // On the server
 * get('foo', { req, res })
 *
 * @see https://github.com/andreizanik/cookies-next/blob/master/README.md#client-and-server
 */
function get(name: string, options?: OptionsType) {
  return getCookie(name, options);
}

/**
 * Create reservation id cookie with expiry set to a day after `seating`
 *
 * @example
 * // On the client
 * addReservationId('foo', new Date())
 * // On the server
 * addReservationId('foo', new Date(), { req, res })
 *
 * @see https://github.com/andreizanik/cookies-next/blob/master/README.md#client-and-server
 */
function addReservationId(id: string, seating: Dayjs, options?: OptionsType) {
  const expires = dateUtil(seating).add(1, 'day');
  return set(`reservation_${id}`, id, null, {
    expires: expires.toDate(),
    ...options,
  });
}

/**
 * Reservation id cookie for a reservation booking-api returned, expiring a day
 * after its seating starts: the business day and start quartile in the venue's
 * timezone. Its seatingDate stands in only for a reservation without those (it
 * is an hour late for Finland and a day early after midnight).
 *
 * @example
 * // On the server
 * addReservation(response.data.result, { req, res })
 */
function addReservation(reservation: Reservation, options?: OptionsType) {
  const seatingStart = seatingStartOrNull(reservation) ?? reservation?.seatingDate?.iso;
  return addReservationId(reservation?.objectId, dateUtil(seatingStart), options);
}

/**
 * Get reservation id cookie by id
 *
 * @example
 * // On the client
 * getReservationId('foo')
 * // On the server
 * getReservationId('foo', { req, res })
 *
 * @see https://github.com/andreizanik/cookies-next/blob/master/README.md#client-and-server
 */
function getReservationId(id: string, options?: OptionsType) {
  return get(`reservation_${id}`, options);
}

/**
 * Get all reservation id cookies
 *
 * @example
 * // On the client
 * getReservationIds()
 * // On the server
 * getReservationIds({ req, res })
 *
 * @see https://github.com/andreizanik/cookies-next/blob/master/README.md#client-and-server
 */
function getReservationIds(options?: OptionsType): string[] {
  const reservationIds = [];
  const allCookies = getCookies(options);
  for (const [key, value] of Object.entries(allCookies)) {
    if (key.startsWith('reservation_')) {
      reservationIds.push(value);
    }
  }
  return reservationIds;
}
function setUserInfo(userInfo) {
  const userInfoString = JSON.stringify(userInfo);
  // Set the cookie without an expiration date
  return set('userInfo', userInfoString);
}

function getUserInfo() {
  const userInfoString = get('userInfo');
  return userInfoString ? JSON.parse(userInfoString) : null;
}

/**
 * Get app session from cookie
 * This is a helper for server-side access to the session
 * 
 * @example
 * // On the server
 * const sessionId = getAppSession({ req, res })
 * 
 * @see https://github.com/andreizanik/cookies-next/blob/master/README.md#client-and-server
 */
function getAppSession(options?: OptionsType): string | null {
  return get('app_session', options) as string | null;
}

/**
 * Set app session cookie
 * This is typically called by the session tracking system
 * 
 * @example
 * setAppSession('session-id-here', { req, res })
 */
function setAppSession(sessionId: string, options?: OptionsType) {
  return set('app_session', sessionId, 365, options); // 1 year expiry
}

const cookies = {
  set,
  get,
  remove,
  addReservationId,
  addReservation,
  getReservationId,
  getReservationIds,
  setUserInfo,
  getUserInfo,
  getAppSession,
  setAppSession,
};

export default cookies;
